import { z } from 'zod';

/* Tipos de alvo da trilha de auditoria — fonte única (DB schema, action-mapping do
   admin-service e validação da query da UI derivam daqui). `unknown` é o sentinel
   p/ rota mutante sem mapping (não deve ocorrer; o gate de cobertura impede). */
export const AUDIT_TARGET_TYPES = [
  'company',
  'user',
  'license',
  'device',
  'plan',
  'negotiated_discount',
  'ticket',
  'staff_user',
  'session',
  'pack',
  'publisher',
  'config',
  'faq',
  /* Versão de documento legal (`legal_document_versions` — ADR-0774). `legal.version_publish` é a
     contraparte administrativa do aceite do usuário: quem publicou qual versão vigente, e quando. */
  'legal',
  'release',
  'notification',
  'unknown',
] as const;

export const auditTargetTypeSchema = z.enum(AUDIT_TARGET_TYPES);
export type AuditTargetType = z.infer<typeof auditTargetTypeSchema>;

/* Query de `GET /v1/admin/audit-log`. `limit`/`cursor` = keyset pagination.
   `targetId` filtra a trilha por um alvo específico (drill-down de uma entidade).
   `action`/`targetType` = filtros de igualdade exata server-side (Select único na UI).
   `q` = busca livre (ILIKE "contém") sobre `action` + `targetId` no servidor. */
export const auditQuerySchema = z.object({
  actorId: z.string().min(1).optional(),
  targetType: auditTargetTypeSchema.optional(),
  action: z.string().min(1).optional(),
  targetId: z.string().min(1).optional(),
  q: z.string().min(1).optional(),
  from: z.coerce.date().optional(),
  to: z.coerce.date().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: z.string().min(1).optional(),
});
export type AuditQuery = z.infer<typeof auditQuerySchema>;

/* Entrada da trilha exposta pelo `GET /v1/admin/audit-log` (ADR-0708). `actorEmail` é
   resolvido por LEFT JOIN em `staff_users` — `null` p/ ator órfão/deletado (a FK não
   cascateia). `reason` (justificativa livre do operador) só existe quando a ação a
   registrou na metadata. `ts` já serializado em ISO sobre o fio. */
export const auditEntryDto = z.object({
  id: z.string(),
  ts: z.string(),
  actorId: z.string(),
  actorEmail: z.string().nullable(),
  action: z.string(),
  targetType: auditTargetTypeSchema,
  targetId: z.string(),
  responseStatus: z.number().int(),
  reason: z.string().optional(),
});
export type AuditEntry = z.infer<typeof auditEntryDto>;

/* Página keyset da trilha (`items` + `nextCursor` opaco). */
export const auditLogPageDto = z.object({
  items: z.array(auditEntryDto),
  nextCursor: z.string().nullish(),
});
export type AuditLogPage = z.infer<typeof auditLogPageDto>;
